import {
  Component,
  ComponentRef,
  Injector,
  Input,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';

import { ComponentRenderer } from '../../renderers/component-renderer';
import { DefaultColumnRendererComponent } from './default-column-renderer.component';
import { CellRenderer } from './cell-renderer';

/**
 * Component that dynamically renders configured CellRenderer components to Columns
 */
@Component({
  selector: 'cbre-cell-renderer',
  template: `<ng-container #Container></ng-container> `,
  imports: [DefaultColumnRendererComponent],
  encapsulation: ViewEncapsulation.None,
})
export class CellRendererComponent<T extends { CellRenderer: Type<CellRenderer<any>> }>
  implements OnInit, ComponentRenderer<CellRenderer<T>>
{
  /**
   * Data to use to render
   */
  private _data: Record<string, any>;
  @Input() set data(data: Record<string, any>) {
    this._data = data;
    if (this.input && this.input.instance) {
      this.input.instance.data = this._data;
    }
  }

  private _config: T;
  /**
   * config
   */
  @Input() public set config(cfg: T) {
    this._config = cfg;
    if (this.input && this.input.instance) {
      this.input.instance.config = this._config;
      this.initHostBindingVars(cfg);
    }
  }
  public get config() {
    return this._config;
  }

  /**
   * ContainerRef to render the configured CellRenderer<T> type
   */
  @ViewChild('Container', { read: ViewContainerRef, static: true }) private container: ViewContainerRef;

  public input: ComponentRef<CellRenderer<T>>;

  constructor(protected parentInjector: Injector) {}

  ngOnInit(): void {
    const rendererType: Type<CellRenderer<any>> = this.resolveComponentType();
    this.mountComponent(rendererType);
  }

  public resolveComponentType(): Type<CellRenderer<any>> {
    return this.config.CellRenderer ?? this.getDefaultComponent();
  }

  public getDefaultComponent(): Type<CellRenderer<any>> {
    return DefaultColumnRendererComponent;
  }

  public mountComponent(component: Type<CellRenderer<T>>) {
    this.input = this.container.createComponent(component, { injector: this.parentInjector });
    this.input.instance.config = this._config;
    this.input.instance.data = this._data;

    // init host binding variables
    this.initHostBindingVars(this._config);
  }

  initHostBindingVars(_config: T): void {
    // placeholder to bind css classes accordingly to config info
  }
}
